// v14at-p4 F4 — ComponentPicker — pure JSX-extract uit AutoMeasureZoneBlock.
//
// GEEN gedragswijziging. Deze component bundelt enkel de JSX die vandaag al
// in AutoMeasureZoneBlock gebruikt wordt (lijnen ~402-480 vóór refactor).
// Zowel de sync-flow (AutoMeasureZoneBlock) als de async-flow
// (ReviewMeasurementDialog) importeren deze component.
//
// v14at-p7 — Twee uitbreidingen (geen bestaand gedrag gewijzigd):
//   1. `showActionsInline` prop (default true). Als false → ComponentPicker
//      rendert enkel het instructie-blok + checkbox-lijst; de Apply/Annuleer
//      knoppen worden door de parent (ReviewMeasurementDialog footer) getoond.
//   2. Nieuwe sub-export `ComponentPickerSummary` die een live samenvatting
//      "X bouwdelen · Y gevelvlakken · Z m²" rendert op basis van de huidige
//      selectie. Bedoeld voor de sticky footer van ReviewMeasurementDialog.
import React, { useMemo } from "react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Home, Warehouse, Umbrella, HelpCircle } from "lucide-react";

// Identieke mapping als AutoMeasureZoneBlock (bewust gedupliceerd om geen
// cirkel-import te creëren).
const COMPONENT_ICON = {
  main: Home,
  main_by_address_anchor: Home,
  main_virtual_facade_aggregate: Home,
  attached_building: Warehouse,
  adjacent_building: Warehouse,
  secondary_detached: Warehouse,
  canopy: Umbrella,
};

export function ComponentPicker({
  building,
  selected,
  onToggle,
  onApply,
  onCancel,
  hasExistingSurfaces = false,
  dataTestIdPrefix = "automeasure-component-picker",
  applyLabels = null, // {keep, replace, first}
  showActionsInline = true, // v14at-p7 — false = parent toont knoppen in sticky footer
}) {
  if (!building) return null;
  const comps = building.components || [];
  const meaningful = comps.filter((c) =>
    (c.physical_facades || []).some((f) => !f.is_shared)
  );

  const labels = applyLabels || {
    keep: "Toevoegen aan bestaande",
    replace: "Vervang bestaande",
    first: "Toepassen",
  };

  return (
    <div
      className="mt-2 rounded-lg bg-white border border-[#6B8E6B]/50 p-3 space-y-3"
      data-testid={dataTestIdPrefix}
    >
      <div>
        <div className="text-[13px] font-semibold text-[#2C2C2A]">
          {meaningful.length} bouwdelen herkend
        </div>
        <div className="text-[11px] text-[#5D7259] mt-0.5">
          Kies welke bouwdelen je in de meting wil opnemen. Je kunt dit later
          altijd manueel aanpassen.
        </div>
      </div>
      <div className="space-y-1.5">
        {comps.map((comp) => {
          const nSurfaces = (comp.physical_facades || []).filter(
            (f) => !f.is_shared && f.is_paintable_default
          ).length;
          if (nSurfaces === 0) return null;
          const Icon = COMPONENT_ICON[comp.role] || HelpCircle;
          const isChecked = selected.has(comp.component_id);
          return (
            <label
              key={comp.component_id}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded border cursor-pointer transition ${
                isChecked
                  ? "bg-[#F2F5EE] border-[#6B8E6B]/60"
                  : "bg-white border-[#E5E5E1] hover:bg-[#FBFBF8]"
              }`}
              data-testid={`${dataTestIdPrefix}-component-${comp.component_id}`}
            >
              <Checkbox
                checked={isChecked}
                onCheckedChange={() => onToggle(comp.component_id)}
                data-testid={`${dataTestIdPrefix}-check-${comp.component_id}`}
              />
              <Icon className="w-4 h-4 text-[#5D7259] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-medium text-[#2C2C2A]">
                  {comp.label}
                </div>
                <div className="text-[10.5px] text-[#5D7259]">
                  {nSurfaces} gevelvlak{nSurfaces === 1 ? "" : "ken"}
                  {comp.footprint_area_m2
                    ? ` · ${comp.footprint_area_m2.toFixed(1)} m² grondopp.`
                    : ""}
                  {comp.classification_confidence === "low" ||
                  comp.classification_confidence === "uncertain"
                    ? " · onzeker"
                    : ""}
                </div>
              </div>
            </label>
          );
        })}
      </div>
      {showActionsInline && (
        <div className="flex flex-wrap gap-2 pt-1">
          {hasExistingSurfaces ? (
            <>
              <Button
                onClick={() => onApply("keep")}
                className="bg-[#6B8E6B] hover:bg-[#5B7D5B] text-white text-xs h-9"
                disabled={selected.size === 0}
                data-testid={`${dataTestIdPrefix}-apply-keep`}
              >
                {labels.keep}
              </Button>
              <Button
                onClick={() => onApply("replace")}
                variant="outline"
                className="border-[#B84B45] text-[#B84B45] hover:bg-[#FDF0EE] text-xs h-9"
                disabled={selected.size === 0}
                data-testid={`${dataTestIdPrefix}-apply-replace`}
              >
                {labels.replace}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => onApply("keep")}
              className="bg-[#6B8E6B] hover:bg-[#5B7D5B] text-white text-xs h-9"
              disabled={selected.size === 0}
              data-testid={`${dataTestIdPrefix}-apply-first`}
            >
              {labels.first}
            </Button>
          )}
          <Button
            onClick={onCancel}
            variant="ghost"
            className="text-xs h-9"
            data-testid={`${dataTestIdPrefix}-cancel`}
          >
            Annuleren
          </Button>
        </div>
      )}
    </div>
  );
}


// v14at-p7 — Live samenvatting van user-selectie. Wordt getoond in de
// sticky footer van ReviewMeasurementDialog zodat de user ALTIJD ziet
// hoeveel bouwdelen/gevelvlakken/m² hij op het punt staat toe te passen.
// Herrekent reactief op elke `onToggle` via `selected`-prop.
export function ComponentPickerSummary({
  building,
  selected,
  dataTestId = "review-measurement-summary",
}) {
  const stats = useMemo(() => {
    let nComp = 0;
    let nSurfaces = 0;
    let area = 0;
    for (const c of building?.components || []) {
      if (!selected?.has(c.component_id)) continue;
      const facades = (c.physical_facades || []).filter(
        (f) => !f.is_shared && f.is_paintable_default
      );
      if (facades.length === 0) continue;
      nComp += 1;
      nSurfaces += facades.length;
      area += facades.reduce((a, f) => a + (f.effective_area_m2 || 0), 0);
    }
    return { nComp, nSurfaces, area: Math.round(area * 100) / 100 };
  }, [selected, building]);

  if (!building) return null;
  if (stats.nComp === 0) {
    return (
      <div
        className="text-[12px] text-[#A33F39]"
        data-testid={`${dataTestId}-empty`}
      >
        Selecteer minstens één bouwdeel om toe te passen.
      </div>
    );
  }
  return (
    <div className="text-[12.5px] text-[#2C2C2A]" data-testid={dataTestId}>
      <strong className="font-mono">{stats.nComp}</strong>{" "}
      {stats.nComp === 1 ? "bouwdeel" : "bouwdelen"}
      {" · "}
      <strong className="font-mono">{stats.nSurfaces}</strong> gevelvlak
      {stats.nSurfaces === 1 ? "" : "ken"}
      {" · "}
      <strong className="font-mono">
        {stats.area.toFixed(2).replace(".", ",")}
      </strong>{" "}
      m²
    </div>
  );
}
